'use client';
import { useEffect, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { useEnabledModules } from '@/hooks/use-enabled-modules';
import { useTranslation } from '@/lib/i18n';

// Longest prefixes first so '/settings/portal/manager' is tested before '/settings/portal'.
const ROUTE_MODULE: Array<[string, string]> = [
  ['/settings/portal/manager/assistant', 'knowledge'],
  ['/settings/portal/employee/assistant', 'knowledge'],
  ['/settings/portal/knowledge', 'knowledge'],
  ['/settings/portal/manager', 'team-portal'],
  ['/settings/integrations', 'integrations'],
  ['/settings/portal', 'user-portal'],
  // Deepfake Simulation's settings sit under Settings, outside '/deepfake'.
  ['/settings/deepfake', 'deepfake'],
  ['/content-studio', 'content-studio'],
  ['/phishtrainer', 'phishtrainer'],
  ['/announcements', 'announcements'],
  // Surveys v2 and Policies (2026-10-12), both opt-in per company. The
  // employee pages live under /portal/surveys and /portal/policies — portal
  // routes, gated by the API's entitlement guards (403 module_disabled) —
  // not here.
  ['/surveys', 'surveys'],
  ['/policies', 'policies'],
  ['/jit-training', 'jit-training'],
  ['/gamification', 'gamification'],
  // Email Security Analyzer and Email Remediator are NOT in this table since 27 Oct 2026 (UX-VISUAL-17):
  // their layouts (app/esa/layout.tsx, app/er/layout.tsx) draw an in-page "not switched on" state with the
  // locked picture instead of a redirect and a toast that is gone in seconds. The API refuses either way.
  ['/phishing', 'phishing'],
  ['/vishing', 'vishing'],
  ['/smishing', 'smishing'],
  ['/deepfake', 'deepfake'],
  ['/reports', 'reports'],
  ['/vcro', 'vcro'],
  // Before '/lms': the classic LMS automation builder is its own module.
  ['/lms/automations', 'lms-automations'],
  ['/lms', 'lms'],
];

export function ModuleAccessGuard({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const enabled = useEnabledModules(); // Set<string> | null
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);
  // legitimate external sync: SSR-hydration signal to avoid redirect before first client render
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => setMounted(true), []);
  /* eslint-enable react-hooks/set-state-in-effect */

  // When the "not enabled" toast was last raised (see the second effect).
  const announcedAt = useRef<number | null>(null);

  useEffect(() => {
    if (!mounted || enabled === null) return; // fail-open: don't redirect while loading/unknown
    const hit = ROUTE_MODULE.find(
      ([prefix]) => pathname === prefix || pathname.startsWith(prefix + '/'),
    );
    if (hit && !enabled.has(hit[1])) {
      // In the admin's language (W1 web review M5): every old /lms/automations
      // bookmark of a company without that module lands here.
      announcedAt.current = Date.now();
      toast.error(t('shell.moduleGuard.notEnabled'), { id: NOT_ENABLED_TOAST_ID });
      router.replace('/');
    }
  }, [mounted, pathname, enabled, router, t]);

  // A bookmark opened cold is redirected before the saved language's catalogue
  // has loaded (the provider renders English first), so the toast went out in
  // English. When the language arrives while the toast is still up, the SAME
  // toast is said again in it — sonner updates a toast with the same id in
  // place, so the reader sees one notice, in their language.
  useEffect(() => {
    const at = announcedAt.current;
    if (at !== null && Date.now() - at < NOT_ENABLED_TOAST_WINDOW_MS) {
      toast.error(t('shell.moduleGuard.notEnabled'), { id: NOT_ENABLED_TOAST_ID });
    }
  }, [t]);

  return <>{children}</>;
}

/** One "this module isn't enabled" toast, updated in place rather than stacked. */
const NOT_ENABLED_TOAST_ID = 'module-access-guard:not-enabled';
/** How long after it is raised the toast is re-said in a language that arrives late. */
const NOT_ENABLED_TOAST_WINDOW_MS = 8_000;
